/**
 * Footer Component - Expert Refactor (Optimized Memo Usage)
 * 
 * Improvements:
 * - Strategic memo usage (only where beneficial)
 * - Custom hooks for background generation and social links
 * - Better separation of concerns
 * - Enhanced accessibility
 * - Performance optimized without over-memoization
 */

import React, { memo, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXTwitter,
  faDiscord,
  faInstagram,
} from "@fortawesome/free-brands-svg-icons";

// Assets
import pwa from "@assets/images/pwa.svg";
import pwr from "@assets/images/pwr.png";

// Context and Utils
import { useAppContext } from "@context/AppContext";
import { useDynamicBackground } from "@hooks/useDynamicBackground";
import constant from "@utils/constant";

// Constants
const CURRENT_YEAR = new Date().getFullYear();
const COMPANY_NAME = "RED.DEV INC.";
const ICON_SIZE = "2.7rem";

// SVG Templates for background
const SVG_TEMPLATES = {
  bottom1: '<svg xmlns="http://www.w3.org/2000/svg" width="1927.493" height="427.067" viewBox="0 0 1927.493 427.067"><path id="Bottom_1" data-name="Bottom 1" d="M6328,3687.655l2-388.015s1526.8-23.511,1920.992,227.675c7.318,323.32,3.511,155.024,3.511,155.024Z" transform="translate(-6328 -3299.055)" fill="__FILL_COLOR__"/></svg>',
  bottom2: '<svg xmlns="http://www.w3.org/2000/svg" width="1927.029" height="861.554" viewBox="0 0 1927.029 861.554"><path id="Bottom_2" data-name="Bottom 2" d="M8252.611,2184.933c-1.19-747.663,0,638.237,0,638.237l-1927.028,1.941,3.487-272.219S8253.8,2932.6,8252.611,2184.933Z" transform="translate(-6325.583 -1963.557)" fill="__FILL_COLOR__"/></svg>'
};

// Simple Social Icon Component (no memo needed - simple and props rarely change)
const SocialIcon = ({ href, icon, color, label }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="tooltip"
    aria-label={label}
  >
    <FontAwesomeIcon
      icon={icon}
      style={{ color }}
      role="img"
      className="social-icon"
    />
  </a>
);

// Simple Logo Section (no memo needed - static content)
const LogoSection = () => (
  <div className="footer__pwb">
    <div className="footer__pwb-inner">
      <div className="footer__pwb-avalanche">
        <img 
          className="front" 
          src={pwa} 
          alt="PWA - Progressive Web App logo" 
          loading="lazy"
        />
      </div>
      <div className="footer__pwb-reddev">
        <img 
          className="back" 
          src={pwr} 
          alt="RED.DEV logo" 
          loading="lazy"
        />
      </div>
    </div>
  </div>
);

// Simple Copyright Component (no memo needed - simple props)
const Copyright = ({ year, company, className }) => (
  <span className={className}>
    © {year} {company} All Rights Reserved.
  </span>
);

// Custom hook for social links with fallbacks
const useSocialLinks = (themeConfig) => {
  return useMemo(() => ({
    instagram: themeConfig?.APP_INSTA_LINK || constant.INSTAGRAM_LINK,
    twitter: themeConfig?.APP_TWITTER_LINK || constant.TWITTER_LINK,
    discord: themeConfig?.APP_DISCORD_LINK || constant.APP_DISCORD_LINK,
  }), [
    themeConfig?.APP_INSTA_LINK,
    themeConfig?.APP_TWITTER_LINK,
    themeConfig?.APP_DISCORD_LINK
  ]);
};

// Main Footer Component - Only memoize the root component
const Footer = memo(() => {
  const { themeConfig } = useAppContext();
  
  // Custom hooks
  const backgroundImage = useDynamicBackground(
    SVG_TEMPLATES,
    themeConfig?.SECONDARY,
    themeConfig?.PRIMARY
  );
  
  const socialLinks = useSocialLinks(themeConfig);
  
  // Memoized values
  const primaryColor = useMemo(() => 
    themeConfig?.PRIMARY || "#333333", 
    [themeConfig?.PRIMARY]
  );

  const footerStyle = useMemo(() => ({
    backgroundImage: backgroundImage || undefined
  }), [backgroundImage]);

  // Memoized mobile social links (uses different constants)
  const mobileSocialLinks = useMemo(() => ({
   instagram: themeConfig?.APP_INSTA_LINK || constant.INSTAGRAM_LINK,
    twitter: themeConfig?.APP_TWITTER_LINK || constant.TWITTER_LINK,
    discord: themeConfig?.APP_DISCORD_LINK || constant.APP_DISCORD_LINK,
  }), [

    themeConfig?.APP_INSTA_LINK,
    themeConfig?.APP_TWITTER_LINK,
    themeConfig?.APP_DISCORD_LINK
  ]);

  // Render social icons - simple function, no memo needed
  const renderSocialIcons = (links, isMobile = false) => {
    const className = isMobile ? "mobile-footer__social-icons" : "footer__social-icons";
    
    return (
      <div className={className} role="list">
        <SocialIcon
          href={links.instagram}
          icon={faInstagram}
          color={primaryColor}
          label="Follow us on Instagram"
        />
        <SocialIcon
          href={links.twitter}
          icon={faXTwitter}
          color={primaryColor}
          label="Follow us on Twitter"
        />
        <SocialIcon
          href={links.discord}
          icon={faDiscord}
          color={primaryColor}
          label="Join our Discord"
        />
      </div>
    );
  };

  return (
    <footer 
      id="footer" 
      style={footerStyle}
      role="contentinfo"
      aria-label="Site footer"
    >
      {/* Desktop Footer */}
      <div className="desktop-footer">
        {renderSocialIcons(socialLinks)}
        
        <div className="footer__copyright">
          <Copyright
            year={CURRENT_YEAR}
            company={COMPANY_NAME}
            className="footer__copyright-text"
          />
          <LogoSection />
        </div>
      </div>

      {/* Mobile Footer */}
      <div className="mobile-footer">
        <div className="footer-group1">
          {renderSocialIcons(mobileSocialLinks, true)}
          <LogoSection />
        </div>
        
        <Copyright
          year={CURRENT_YEAR}
          company={COMPANY_NAME}
          className="footer__copyright-text"
        />
      </div>
    </footer>
  );
});

Footer.displayName = 'Footer';

export default Footer;
